import { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { format } from 'date-fns';
import { useCurrency } from '../store/currency';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const PIE_COLORS = ['#1a5fa8','#2d7a4f','#7a5f28','#b83232','#6b3fa0','#2d6e7a','#7a3f28','#3d7a28'];

function ChartTooltip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--stone)', borderRadius: 8, padding: '10px 14px', boxShadow: 'var(--shadow)' }}>
      <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 4 }}>{label}</div>
      {payload.map(p => <div key={p.name} style={{ fontSize: 12.5, color: p.color || 'var(--ink)', marginBottom: 2 }}>{p.name}: {fmt(p.value)}</div>)}
    </div>
  );
}

export default function Dashboard() {
  const year = new Date().getFullYear();
  const month = new Date().getMonth() + 1;
  const navigate = useNavigate();
  const { fmt, currency } = useCurrency();
  const isMobile = useIsMobile();

  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [categories, setCategories] = useState([]);
  const [liveNW, setLiveNW] = useState(null);
  const [loanSummary, setLoanSummary] = useState(null);

  useEffect(() => {
    const load = async () => {
      // Load core data first — these must succeed
      try {
        const [sum, cats] = await Promise.all([
          api.monthlySummary(year),
          api.getCategories(),
        ]);
        setSummary(sum);
        setCategories(cats);
      } catch (e) { console.error('Core data failed', e); }

      // Load secondary data independently — failures don't block dashboard
      try { setLiveNW(await api.getLiveNetWorth()); } catch {}
      try { setLoanSummary(await api.getLoansSummary()); } catch {}
      try { setForecast(await api.getForecast(year)); } catch {}
    };
    load();
  }, []);

  // Build monthly bar chart
  const barData = MONTHS.map((m, i) => {
    const monthData = summary?.[String(i + 1)] || {};
    const income = Object.entries(monthData).filter(([,v]) => v > 0).reduce((a,[,v]) => a + v, 0);
    const expenses = Math.abs(Object.entries(monthData).filter(([,v]) => v < 0).reduce((a,[,v]) => a + v, 0));
    return { name: m, Income: income, Expenses: expenses };
  });

  // Current month stats
  const thisMonth = summary?.[String(month)] || {};
  const monthIncome = Object.entries(thisMonth).filter(([,v]) => v > 0).reduce((a,[,v]) => a + v, 0);
  const monthExpenses = Math.abs(Object.entries(thisMonth).filter(([,v]) => v < 0).reduce((a,[,v]) => a + v, 0));
  const monthNet = monthIncome - monthExpenses;

  // Spending by category (this month)
  const catSpending = Object.entries(thisMonth)
    .filter(([,v]) => v < 0)
    .map(([name, val]) => ({ name, value: Math.abs(val) }))
    .sort((a,b) => b.value - a.value);

  // Budget alerts
  const budgetAlerts = categories
    .filter(c => c.budget_monthly && thisMonth[c.name] < 0)
    .map(c => ({
      ...c,
      spent: Math.abs(thisMonth[c.name] || 0),
      pct: Math.abs(thisMonth[c.name] || 0) / c.budget_monthly * 100,
    }))
    .filter(c => c.pct > 70)
    .sort((a,b) => b.pct - a.pct);

  // Cumulative balance for area chart
  const cumData = forecast?.months?.map(m => ({
    name: MONTHS[m.month - 1],
    Balance: m.cumulative_balance,
  })) || [];

  return (
    <div className="animate-in">
      <div className="page-eyebrow">{format(new Date(), 'EEEE, MMMM d')}</div>
      <div className="page-title" style={{ marginBottom: 4 }}>Dashboard</div>
      <div className="page-subtitle">Your financial picture at a glance</div>

      {/* KPIs */}
      <div className="grid-4 mb-6">
        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/networth')}>
          <div className="stat-label">Net Worth</div>
          <div className="stat-value" style={{ color: liveNW?.net_worth != null ? (liveNW.net_worth >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--mid)' }}>
            {liveNW?.net_worth != null ? fmt(liveNW.net_worth) : '—'}
          </div>
          <div className="stat-sub">{liveNW?.data_complete ? 'Live estimate' : 'Tap to set up →'}</div>
        </div>
        <div className="card">
          <div className="stat-label">This Month — Net</div>
          <div className={`stat-value ${monthNet >= 0 ? 'green' : 'red'}`}>{monthNet >= 0 ? '+' : '−'}{fmt(monthNet)}</div>
          <div className="stat-sub">{fmt(monthIncome)} in · {fmt(monthExpenses)} out</div>
        </div>
        <div className="card">
          <div className="stat-label">Year-End Projection</div>
          <div className="stat-value gold">{forecast ? fmt(forecast.summary?.projected_year_end_balance ?? 0) : '—'}</div>
          <div className="stat-sub">Cumulative net balance</div>
        </div>
        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/loans')}>
          <div className="stat-label">Monthly Debt Payments</div>
          <div className="stat-value red">{loanSummary ? fmt(loanSummary.total_monthly_payment) : '—'}</div>
          <div className="stat-sub">{loanSummary?.loan_count ?? 0} loans · {loanSummary ? fmt(loanSummary.total_balance) : '—'} outstanding</div>
        </div>
      </div>

      {/* Budget alerts */}
      {budgetAlerts.length > 0 && (
        <div className="card mb-6" style={{ borderLeft: '3px solid var(--gold)', padding: '16px 20px' }}>
          <div className="section-title mb-3" style={{ color: 'var(--gold)' }}>! Budget Alerts — {format(new Date(), 'MMMM')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {budgetAlerts.map(c => (
              <div key={c.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{c.icon} {c.name}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: c.pct >= 100 ? 'var(--red)' : 'var(--gold)' }}>
                    {fmt(c.spent)} / {fmt(c.budget_monthly)} ({c.pct.toFixed(0)}%)
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{
                    width: `${Math.min(c.pct, 100)}%`,
                    background: c.pct >= 100 ? 'var(--red)' : 'var(--gold)',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid-2 mb-6">
        {/* Bar chart */}
        <div className="card">
          <div className="section-title mb-4">Income vs Expenses — {year}</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={barData} barSize={10} barGap={3}>
              <XAxis dataKey="name" tick={{ fill: 'var(--mid)', fontSize: 10.5 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={(props) => <ChartTooltip {...props} fmt={fmt} />} />
              <Bar dataKey="Income" fill="var(--green-bg)" stroke="var(--green)" strokeWidth={1} radius={[3,3,0,0]} />
              <Bar dataKey="Expenses" fill="var(--red-bg)" stroke="var(--red)" strokeWidth={1} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cumulative trajectory */}
        <div className="card">
          <div className="section-title mb-4">Cumulative Balance Trajectory</div>
          {cumData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={cumData}>
                <defs>
                  <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--green)" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="var(--green)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fill: 'var(--mid)', fontSize: 10.5 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={(props) => <ChartTooltip {...props} fmt={fmt} />} />
                <Area type="monotone" dataKey="Balance" stroke="var(--green)" strokeWidth={2} fill="url(#cumGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180, color: 'var(--mid)', fontSize: 13 }}>
              Upload transactions to see forecast
            </div>
          )}
        </div>
      </div>

      {/* Spending pie + category list */}
      <div className="card">
        <div className="section-title mb-4">Spending Breakdown — {format(new Date(), 'MMMM yyyy')}</div>
        {catSpending.length > 0 ? (
          <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
            <PieChart width={160} height={160}>
              <Pie data={catSpending.slice(0,8)} cx={75} cy={75} innerRadius={48} outerRadius={74} dataKey="value" paddingAngle={2}>
                {catSpending.slice(0,8).map((_,i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
            </PieChart>
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '4px' : '6px 24px' }}>
              {catSpending.slice(0,8).map((c, i) => {
                const cat = categories.find(x => x.name === c.name);
                const budget = cat?.budget_monthly;
                const pct = budget ? (c.value / budget * 100) : null;
                return (
                  <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12.5, color: 'var(--ink2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 500, flexShrink: 0, marginLeft: 8 }}>{fmt(c.value)}</span>
                      </div>
                      {pct != null && (
                        <div className="progress-bar" style={{ marginTop: 3 }}>
                          <div className="progress-fill" style={{ width: `${Math.min(pct, 100)}%`, background: pct >= 100 ? 'var(--red)' : PIE_COLORS[i % PIE_COLORS.length] + '88' }} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--mid)', fontSize: 13 }}>
            No transactions this month yet
          </div>
        )}
      </div>
    </div>
  );
}
